import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

interface ExecutiveSummaryProps {
  name: string;
}

interface AudioData {
  url: string;
  generatedAt: any;
}

interface SummaryData {
  content: string;
  generatedAt: any;
}

export function ExecutiveSummary({ name }: ExecutiveSummaryProps) {
  const { currentUser } = useAuth();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const progressRef = React.useRef<HTMLDivElement>(null);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Cleanup function to stop audio and reset states
  const cleanupAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  useEffect(() => {
    const loadReportData = async () => {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          
          // Parse summary text
          if (data.executive_summary_text) {
            try {
              const summaryData = JSON.parse(data.executive_summary_text) as SummaryData;
              setSummaryText(summaryData.content);
            } catch (error) {
              console.error('Error parsing summary text:', error);
              setSummaryText(null);
            }
          }
          
          // Parse audio URL
          if (data.executive_summary_audio_url) {
            try {
              const audioData = JSON.parse(data.executive_summary_audio_url) as AudioData;
              setAudioUrl(audioData.url);
              // Create and load audio element
              const audio = new Audio(audioData.url);
              audio.preload = 'metadata';
              audioRef.current = audio;
              
              // Set up event listeners
              audio.addEventListener('loadedmetadata', handleLoadedMetadata);
              audio.addEventListener('timeupdate', handleTimeUpdate);
              audio.addEventListener('ended', handleAudioEnded);
            } catch (error) {
              console.error('Error parsing audio URL:', error);
              setAudioUrl(null);
            }
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading report data:', error);
        setIsLoading(false);
      }
    };

    loadReportData();
    
    // Cleanup function
    return () => {
      if (audioRef.current) {
        cleanupAudio();
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current = null;
      }
    };
  }, [currentUser]);

  // Additional effect to handle unmounting
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      console.log('Audio duration loaded:', audioRef.current.duration);
      setIsAudioLoaded(true);
      setIsLoading(false);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current && audioRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-6 py-8 bg-gray-50 border-b border-gray-200"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
      {!summaryText ? (
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-600">
            Executive summary will be available once your report is generated.
          </p>
        </div>
      ) : (
        <div className="prose max-w-none text-gray-600 space-y-4">
          <p>{summaryText.replace('{name}', name)}</p>
        </div>
      )}

        {/* Audio Player Section */}
        <div className={`mt-8 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-xl p-6 backdrop-blur-sm ${!audioUrl ? 'opacity-50' : ''}`}>
          <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
            <Volume2 className="w-5 h-5 mr-2 text-primary" />
            Listen to your feedback summary
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePlayPause}
                disabled={!audioUrl || isLoading || !isAudioLoaded}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-primary hover:bg-primary-hover transition-colors text-dark disabled:opacity-50"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6 ml-1" />
                )}
              </button>
              
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {!audioUrl ? 'Audio summary will be available once your report is generated' :
                   isLoading ? 'Loading audio...' :
                   !isAudioLoaded ? 'Preparing audio...' : 'Click play to listen'}
                </div>         
              </div>
            </div>

            {/* Progress Bar */}
            <div className={`space-y-2 ${!isAudioLoaded ? 'opacity-50' : ''}`}>
              <div 
                ref={progressRef}
                className={`h-2 bg-gray-200 rounded-full relative overflow-hidden ${isAudioLoaded ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                onClick={handleProgressClick}
              >
                <div 
                  className="absolute top-0 left-0 h-full bg-primary transition-all duration-100"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>

        </div>
    </motion.div>
  );
}